import noop from 'lodash/noop'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'

const clickSound = require('../../assets/sounds/click.wav')
const moveSound = require('../../assets/sounds/move.wav')
const fightSound = require('../../assets/sounds/fight.wav')
const deploySound = require('../../assets/sounds/deploy.wav')
const withdrawSound = require('../../assets/sounds/withdraw.wav')
const switchSound = require('../../assets/sounds/switch.wav')
const timeSound = require('../../assets/sounds/time.wav')

export interface SoundContextType {
  playClickSound: () => void
  playMoveSound: () => void
  playFightSound: () => void
  playDeploySound: () => void
  playWithdrawSound: () => void
  playSwitchSound: () => void
  playTimeSound: () => void
}

const SoundContext = createContext<SoundContextType>({
  playClickSound: noop,
  playFightSound: noop,
  playDeploySound: noop,
  playMoveSound: noop,
  playSwitchSound: noop,
  playTimeSound: noop,
  playWithdrawSound: noop,
})

export const SoundProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const clickAudioRef = useRef<HTMLAudioElement>(null)
  const [userInteracted, setUserInteracted] = useState(false)

  const moveAudioRef = useRef<HTMLAudioElement>(null)
  const fightAudioRef = useRef<HTMLAudioElement>(null)
  const deployAudioRef = useRef<HTMLAudioElement>(null)
  const withdrawAudioRef = useRef<HTMLAudioElement>(null)
  const switchAudioRef = useRef<HTMLAudioElement>(null)
  const timeAudioRef = useRef<HTMLAudioElement>(null)

  const playClickSound = () => userInteracted && clickAudioRef.current?.play()
  const playMoveSound = () => userInteracted && moveAudioRef.current?.play()
  const playFightSound = () => userInteracted && fightAudioRef.current?.play()
  const playDeploySound = () => userInteracted && deployAudioRef.current?.play()
  const playWithdrawSound = () => userInteracted && withdrawAudioRef.current?.play()
  const playSwitchSound = () => userInteracted && switchAudioRef.current?.play()
  const playTimeSound = () => userInteracted && timeAudioRef.current?.play()

  useEffect(() => {
    const handleUserInteraction = () => {
      setUserInteracted(true)
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
    }

    document.addEventListener('click', handleUserInteraction)
    document.addEventListener('keydown', handleUserInteraction)

    return () => {
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
    }
  }, [])

  return (
    <SoundContext.Provider
      value={{
        playClickSound,
        playTimeSound,
        playFightSound,
        playDeploySound,
        playMoveSound,
        playSwitchSound,
        playWithdrawSound,
      }}
    >
      <audio ref={clickAudioRef} src={clickSound} />
      <audio ref={moveAudioRef} src={moveSound} />
      <audio ref={fightAudioRef} src={fightSound} />
      <audio ref={deployAudioRef} src={deploySound} />
      <audio ref={withdrawAudioRef} src={withdrawSound} />
      <audio ref={switchAudioRef} src={switchSound} />
      <audio ref={timeAudioRef} src={timeSound} />
      {children}
    </SoundContext.Provider>
  )
}

export const useSoundContext = () => useContext(SoundContext)
